import { useReducedMotion } from "@lib/prefersReducedMotion";
import gsap from "gsap";
import { ScrollTrigger } from "./lenis";

type Direction = "up" | "down" | "left" | "right" | "fade";

const D = 50;

function dirToTransform(dir: Direction): { x?: number; y?: number } {
  switch (dir) {
    case "down":
      return { y: -D };
    case "left":
      return { x: D };
    case "right":
      return { x: -D };
    case "fade":
      return {};
    default:
      return { y: D };
  }
}

function reveal(el: HTMLElement, delay = 0): void {
  if (el.dataset.revealed) return;
  el.dataset.revealed = "1";

  if (useReducedMotion()) {
    gsap.set(el, { opacity: 1, x: 0, y: 0 });
    el.classList.add("reveal-active");
    return;
  }

  const dir = (el.dataset.reveal as Direction) || "up";
  const t = dirToTransform(dir);
  gsap.fromTo(
    el,
    { opacity: 0, ...t },
    {
      opacity: 1,
      x: 0,
      y: 0,
      duration: 1.1,
      delay,
      ease: "expo.out",
      onStart: () => el.classList.add("reveal-active"),
      clearProps: "transform,will-change",
    },
  );
}

/**
 * Activate the universal [data-reveal] system on the entire DOM.
 * Call once after the page is mounted.
 *
 * Supports:
 *   data-reveal="up|down|left|right|fade"
 *   data-reveal-delay="0.2" (seconds)
 *   data-reveal-stagger="0.08" on parent — auto-staggers children
 */
export function initReveals(root: ParentNode = document): void {
  const reducedMotion = useReducedMotion();

  // Initial state — hide everything that will reveal
  root.querySelectorAll<HTMLElement>("[data-reveal]").forEach((el) => {
    const dir = (el.dataset.reveal as Direction) || "up";
    if (reducedMotion) {
      gsap.set(el, { opacity: 1, x: 0, y: 0 });
    } else {
      gsap.set(el, { opacity: 0, ...dirToTransform(dir) });
    }
  });

  // Individual reveals (skip children of staggered parents — handled below)
  root
    .querySelectorAll<HTMLElement>("[data-reveal]:not([data-reveal-stagger] [data-reveal])")
    .forEach((el) => {
      const delay = Number.parseFloat(el.dataset.revealDelay || "0");
      ScrollTrigger.create({
        trigger: el,
        start: "top 88%",
        once: true,
        onEnter: () => reveal(el, delay),
      });
    });

  // Staggered groups
  root.querySelectorAll<HTMLElement>("[data-reveal-stagger]").forEach((parent) => {
    const stagger = Number.parseFloat(parent.dataset.revealStagger || "0.08");
    const kids = Array.from(parent.querySelectorAll<HTMLElement>("[data-reveal]"));
    if (!kids.length) return;
    ScrollTrigger.create({
      trigger: parent,
      start: "top 85%",
      once: true,
      onEnter: () => {
        kids.forEach((k, i) => reveal(k, i * stagger));
      },
    });
  });
}
